import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { KeyRound, Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
    { label: "Services", href: "#services", testid: "nav-link-services" },
    { label: "Simulateur", href: "#simulateur", testid: "nav-link-simulator" },
    { label: "Avis", href: "#avis", testid: "nav-link-testimonials" },
    { label: "À propos", href: "/a-propos", testid: "nav-link-about" },
    { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const go = (href) => {
        setOpen(false);
        if (href.startsWith("/")) {
            navigate(href);
            window.scrollTo(0, 0);
            return;
        }
        if (document.querySelector(href)) {
            scrollToSection(href);
        } else {
            window.location.href = `/${href}`;
        }
    };

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 left-0 right-0 z-50 border-b border-[#8A7563]/15 bg-[#FAF8F5]/80 backdrop-blur-xl"
        >
            <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
                <button
                    data-testid="navbar-logo"
                    onClick={() => go("#hero")}
                    className="flex items-center gap-2.5 group"
                >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#8A7563]/40 bg-[#8A7563]/10 text-[#8A7563] transition-colors duration-300 group-hover:bg-[#8A7563] group-hover:text-[#FAF8F5]">
                        <KeyRound size={16} strokeWidth={1.75} />
                    </span>
                    <span className="font-display text-2xl font-semibold tracking-wide">
                        Gestion <span className="italic text-[#8A7563]">Séjoura</span>
                        <span className="ml-2 hidden font-mono-accent text-[9px] uppercase tracking-[0.3em] text-[#8A7F72] sm:inline">
                            Conciergerie
                        </span>
                    </span>
                </button>

                <div className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.href}
                            data-testid={l.testid}
                            onClick={() => go(l.href)}
                            className="text-sm text-[#8A7F72] transition-colors duration-300 hover:text-[#8A7563]"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="navbar-cta-button"
                        onClick={() => go("#simulateur")}
                        className="rounded-full border border-[#8A7563]/50 px-5 py-2 text-sm font-medium text-[#8A7563] transition-all duration-300 hover:bg-[#8A7563] hover:text-[#FAF8F5]"
                    >
                        Estimation gratuite
                    </button>
                </div>

                <button
                    data-testid="navbar-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className="text-[#8A7563] lg:hidden"
                    aria-label="Menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </nav>

            {open && (
                <div className="border-t border-[#8A7563]/15 bg-[#FAF8F5]/95 px-6 py-4 backdrop-blur-xl lg:hidden">
                    {LINKS.map((l) => (
                        <button
                            key={l.href}
                            data-testid={`${l.testid}-mobile`}
                            onClick={() => go(l.href)}
                            className="block w-full py-3 text-left text-sm text-[#8A7F72] transition-colors hover:text-[#8A7563]"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="navbar-cta-button-mobile"
                        onClick={() => go("#simulateur")}
                        className="mt-2 w-full rounded-full border border-[#8A7563]/50 px-5 py-2.5 text-sm font-medium text-[#8A7563]"
                    >
                        Estimation gratuite
                    </button>
                </div>
            )}
        </motion.header>
    );
}
